import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';

import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';
import { ContactService } from '../../../services/contact.service';
import { ContactTypeService } from '../../../services/contact-type.service';

import { user } from '../../../models/user.model';
import { Contact } from '../../../models/contact.model';
import { ContactType } from '../../../models/contactType.model';

import { FiledropComponent } from '../../filedrop/filedrop.component';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ActivityService } from '../../../services/activity.service';
import { UserService } from '../../../services/user.service';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DialogManagerService } from 'src/app/services/dialog-manager.service';
import { DialogResult } from 'src/app/workflow/models/dialog-result.model';

@Component({
  selector: 'app-create-contact-dialog',
  standalone: true,
  imports: [
    DropdownModule,
    DialogModule,
    EditorModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    CommonModule,
    FiledropComponent,
    CalendarModule,
    InputTextModule,
    FloatLabelModule,
  ],
  templateUrl: './create-contact-dialog.component.html',
  styleUrl: './create-contact-dialog.component.scss',
})
export class CreateContactDialogComponent implements OnInit {
  @ViewChild(FiledropComponent) fileDropComponent!: FiledropComponent;

  @Input() dialogVisible: boolean = false;

  @Output() dialogVisibleChange = new EventEmitter<boolean>();
  @Output() addLeadDialogVisible = new EventEmitter<boolean>();

  isSubmitting: boolean = false;

  usersLoading: boolean = false;

  currentUserId: number;
  currentUserName: string;

  participants: user[] = [];
  contactTypes: ContactType[] = [];

  contactForm: FormGroup;

  selectedUser: user | null = null;

  constructor(
    private authService: AuthService,
    private contactService: ContactService,
    private contactTypeService: ContactTypeService,
    private notificationService: NotificationService,
    private activityService: ActivityService,
    private userService: UserService,
    private fb: FormBuilder,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private dialogManager: DialogManagerService
  ) {
    this.contactForm = this.fb.group({
      contactType: ['', Validators.required],
      participant: ['', Validators.required],
      contactDate: ['', Validators.required],
      contactTime: ['', Validators.required],
      duration: ['', Validators.required],
      remarks: [''],
      contactBy: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.selectedUser = this.config.data?.selectedUser ?? null;

    this.currentUserId = this.authService.getCurrentUserId();
    this.currentUserName = this.authService.getUserName();

    if (this.selectedUser) {
      this.contactForm.get('participant')?.setValue(this.selectedUser.userId);
      this.contactForm.get('participant')?.disable();
      this.contactForm.get('participant')?.clearValidators();
    }

    this.loadParticipants();
    this.loadContactTypes();
    this.setDefaultDateTime();
    this.setDefaultUser();
  }

  loadParticipants(): void {
    this.userService.getUsersObservable().subscribe((users) => {
      (this.participants = users.filter((user) =>
        user.roles.some((role) => role.roleId === 1)
      )),
        (this.usersLoading = false);
    });
  }

  showAddLeadDialog(): void {
    this.addLeadDialogVisible.emit(true);
  }

  setDefaultDateTime(): void {
    const now = new Date();
    this.contactForm.controls['contactDate'].setValue(now);
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    this.contactForm.controls['contactTime'].setValue(`${hours}:${minutes}`);
    this.contactForm.controls['duration'].setValue('00:30');
  }

  loadContactTypes(): void {
    this.contactTypeService.getContactTypes().subscribe({
      next: (response) => (this.contactTypes = response),
      error: () => console.log('Fehler beim Laden der Kontaktarten'),
    });
  }

  setDefaultUser(): void {
    this.contactForm.controls['contactBy'].setValue(this.currentUserName);
  }

  closeDialog(result?: DialogResult) {
    // this.ref.close();
    this.dialogManager.closeContactDialog(result);
    this.contactForm.reset();
    this.setDefaultDateTime();
    this.setDefaultUser();
    this.dialogVisible = false;
    this.dialogVisibleChange.emit(this.dialogVisible);
  }

  onFileUploadStarted() {
    this.isSubmitting = true;
  }

  onFilesUploaded(fileUrls: string[]) {
    const formValues: Contact = {
      contactTypeId: this.contactForm.value.contactType,
      contactWith:
        this.selectedUser?.userId ?? this.contactForm.value.participant,
      createdBy: this.currentUserId,
      remarks: this.contactForm.value.remarks,
      contactDate: this.contactForm.value.contactDate,
      contactTime: this.contactForm.value.contactTime,
      duration: this.contactForm.value.duration,
      fileUrls: fileUrls,
    };
    this.contactService.saveContact(formValues).subscribe({
      next: (activity) => {
        this.closeDialog({ success: true, activityId: activity.activityId });
        this.activityService.addActivity(activity);
        this.notificationService.success(
          `Aktivität "Kontakt" erfolgreich erstellt`,
          activity
        );
      },
      error: () => {
        this.notificationService.error(`Aktivitat "Kontakt" NICHT erstellt`);
      },
    });

    this.isSubmitting = false;
  }

  contactDialogSubmit(): void {
    this.isSubmitting = true;
    this.fileDropComponent.requestUpload();
  }
}
